import { useMemo } from 'react';
import { useAuth, type AccountType } from './useAuth';

/**
 * Targeting context for the self-advertising promo cards. Derived from the auth
 * session so each promo can decide (via its `show(ctx)` predicate) whether it's
 * relevant to the current visitor. Keeping promos relevant is what makes them
 * read as helpful platform chrome rather than spam.
 */
export interface PromoContext {
  /** A session exists (any account type). */
  isAuthed: boolean;
  /** 'individual' | 'broker' | 'company' | null */
  accountType: AccountType | null;
  /** Raw backend role (e.g. USER, BROKER, COMPANY_ADMIN, AGENT) — may be null. */
  role: string | null;
  /**
   * The visitor already lists properties (broker/company/agent), so supply-side
   * nudges like "list your property" don't apply to them.
   */
  isLister: boolean;
}

const LISTER_ROLE_PATTERN = /BROKER|COMPANY|AGENT/i;

export function usePromoContext(): PromoContext {
  const { user } = useAuth();

  return useMemo<PromoContext>(() => {
    const accountType = (user?.accountType ?? null) as AccountType | null;
    const role = (user?.role ?? null) as string | null;
    const isLister =
      accountType === 'broker' ||
      accountType === 'company' ||
      (!!role && LISTER_ROLE_PATTERN.test(role));

    return {
      isAuthed: !!user,
      accountType,
      role,
      isLister,
    };
  }, [user]);
}
